import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { type RootState } from '../../../redux/store';
import type GuidelinesTypes from './guidelinesTypes';

import { getStoreGuidelines } from '../../../js/common/api';
import { parseKnownGuidelinesParts } from '../../../js/common/utils';
import { isErrorCodes } from './guidelinesUtils';

export const fetchGuidelines = createAsyncThunk('store/guidelines/fetch',
  async (storeIds: number | number[]) =>
    getStoreGuidelines(Array.isArray(storeIds) ? storeIds : [storeIds]));

type GuidelinesState = {
  fetchPending: boolean,
  lastUpdateTime?: number,
  guidelinesInfo: GuidelinesTypes.GuidelinesInfo,
};

const initialState: GuidelinesState = {
  fetchPending: false,
  lastUpdateTime: undefined,
  guidelinesInfo: {},
};

const guidelinesReducer = createSlice({
  name: 'guidelines',
  initialState,
  reducers: null,
  extraReducers: (builder) => {
    builder.addCase(fetchGuidelines.fulfilled, (state, action) => {
      state.fetchPending = false;
      state.lastUpdateTime = Date.now();

      const {
        meta: { arg: storeIds },
        payload: { data: result },
      } = action;

      if (isErrorCodes(result)) {
        // spread the main error into every storeId
        state.guidelinesInfo = [storeIds].flat().reduce((acc, storeId) => ({
          ...acc,
          [storeId]: result,
        }), state.guidelinesInfo);
      } else {
        state.guidelinesInfo = result.reduce((acc, item) => ({
          ...acc,
          [item.id]: {
            ...item,
            parts: parseKnownGuidelinesParts(item.guidelines),
          },
        }), state.guidelinesInfo);
      }
    });

    builder.addCase(fetchGuidelines.pending, (state) => {
      state.fetchPending = true;
    });

    builder.addCase(fetchGuidelines.rejected, (state) => {
      state.fetchPending = false;
      state.lastUpdateTime = null;
    });
  },
});

export const selectGuidelinesForStore =
  (storeId: number) => (state: RootState) => state.guidelines.guidelinesInfo?.[storeId];

export const selectGuidelinesLoading = (state: RootState) => state.guidelines.fetchPending;

export default guidelinesReducer.reducer;
